import { useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Collapse,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import { useSnackbar } from "notistack";
import DashboardHeaderSection from "../common/dashboardheader/DashboardHeader";
import {
  useWhatsappCampaigns,
  useRunWhatsappCampaign,
  useDeleteWhatsappCampaign,
  useOptedOutConversations,
  useClearOptOut,
  useWhatsappNumbers,
} from "../../hooks/useWhatsapp";

function formatDateTime(d) {
  if (!d) return "Never";
  return new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
}

// Delivery status per recipient — read live off WhatsappConversation by the backend
// (routes/whatsappCampaigns.js's enrichCampaignsWithLiveStatus), not stored/stale on the
// campaign itself. "sent" is the default the moment Meta accepts the send; delivered/read
// arrive later via the webhook, so this can legitimately still say "sent" a while after.
const MESSAGE_STATUS = {
  sent: { label: "Sent", icon: DoneRoundedIcon, color: "#6B7280" },
  delivered: { label: "Delivered", icon: DoneAllRoundedIcon, color: "#6B7280" },
  // WhatsApp's own recognizable "read" blue-tick color — kept as-is rather than a brand
  // token since it's a universally-understood WhatsApp UX signal, not a Meesha accent.
  read: { label: "Read", icon: DoneAllRoundedIcon, color: "#53BDEB" },
  failed: { label: "Failed", icon: ErrorOutlineRoundedIcon, color: "#E34C14" },
};

// Meta's documented quality values (see helpers/whatsappHelper.js's BAD_PHONE_QUALITY) —
// anything outside this map still renders, just in a neutral color, rather than breaking.
const QUALITY_COLORS = {
  GREEN: { bg: "rgba(9,172,49,0.12)", color: "#09AC31" },
  YELLOW: { bg: "rgba(250,153,5,0.14)", color: "#B36B00" },
  RED: { bg: "rgba(227,76,20,0.10)", color: "#E34C14" },
  FLAGGED: { bg: "rgba(227,76,20,0.10)", color: "#E34C14" },
  RESTRICTED: { bg: "rgba(227,76,20,0.10)", color: "#E34C14" },
};

function StatusBadge({ status }) {
  const s = MESSAGE_STATUS[status] || MESSAGE_STATUS.sent;
  const Icon = s.icon;
  return (
    <Stack direction="row" spacing={0.4} alignItems="center">
      <Icon sx={{ fontSize: 15, color: s.color }} />
      <Typography sx={{ fontSize: 12.5, color: s.color, fontWeight: status === "read" ? 700 : 500 }}>{s.label}</Typography>
    </Stack>
  );
}

// Top-of-page KPI tiles, WATI-broadcast-style — the headline numbers a sender actually
// cares about at a glance, aggregated across every run of every campaign (not just the
// most recent one), so "how has this WhatsApp channel performed overall" has an answer
// without opening each campaign individually.
function StatCard({ icon: Icon, label, value, sub, bg, color }) {
  return (
    <Box
      sx={{
        flex: "1 1 150px",
        minWidth: 148,
        borderRadius: "14px",
        bgcolor: "#fff",
        border: "1px solid rgba(0,0,0,0.06)",
        p: 1.75,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ width: 28, height: 28, borderRadius: "9px", bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon sx={{ fontSize: 15, color }} />
        </Box>
        <Typography sx={{ fontSize: 11, color: "#9CA3AF", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</Typography>
      </Stack>
      <Typography sx={{ fontSize: 23, fontWeight: 800, fontFamily: "Quicksand", lineHeight: 1.1 }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: 11.5, color: "#9CA3AF" }}>{sub}</Typography>}
    </Box>
  );
}

function QualityChip({ rating }) {
  if (!rating) return null;
  const c = QUALITY_COLORS[rating] || { bg: "#F1F1F1", color: "#666" };
  return <Chip size="small" label={rating} sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, fontSize: 10 }} />;
}

// The connected number(s) this channel actually sends from, with the health signals a
// send failure would otherwise leave unexplained — same data the Templates page surfaces,
// shown here too since a campaign's ability to send lives or dies on this number's state.
function NumberHealthStrip({ numbers }) {
  if (!numbers?.length) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 3 }}>
      {numbers.map((n) => {
        const nearLimit = n.recentUniqueRecipients >= n.messagingLimit * 0.8;
        return (
          <Box
            key={n.phoneNumberId}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.9,
              borderRadius: "10px",
              bgcolor: "#fff",
              border: "1px solid rgba(0,0,0,0.07)",
            }}
          >
            <PhoneIphoneOutlinedIcon sx={{ fontSize: 15, color: "#6B7280" }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, fontFamily: "Quicksand" }}>{n.displayPhoneNumber}</Typography>
            <QualityChip rating={n.qualityRating} />
            {n.currentTier && <Typography sx={{ fontSize: 11, color: "#9CA3AF" }}>{n.currentTier}</Typography>}
            <Typography sx={{ fontSize: 11, color: nearLimit ? "#E34C14" : "#9CA3AF", fontWeight: nearLimit ? 700 : 400 }}>
              {n.recentUniqueRecipients}/{n.messagingLimit} in 24h
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function audienceLabel(campaign) {
  if (campaign.audienceType === "manual") {
    const count = campaign.manualTargets?.length || 0;
    return { label: `${count} selected`, sub: "Manual list" };
  }
  return campaign.planId?.name
    ? { label: campaign.planId.name, sub: "Live plan audience" }
    : { label: "Plan deleted", sub: "Audience no longer available" };
}

// A campaign has no single stored "status" field — this reads its real state off runs/
// runInProgress the same way a person would explain it out loud, so the table's Status
// column always matches what actually happened rather than a stale label.
function campaignStatus(campaign) {
  if (campaign.runInProgress) return { label: "Sending…", bg: "rgba(37,99,235,0.12)", color: "#2563EB" };
  if (!campaign.runs?.length) return { label: "Never run", bg: "#F1F1F1", color: "#666" };
  const last = campaign.runs[campaign.runs.length - 1];
  if (last.failedCount && !last.sentCount) return { label: "Failed", bg: "rgba(227,76,20,0.10)", color: "#E34C14" };
  if (last.failedCount) return { label: "Partial", bg: "rgba(250,153,5,0.14)", color: "#B36B00" };
  return { label: "Completed", bg: "rgba(9,172,49,0.12)", color: "#09AC31" };
}

// One-line "why" for the Status column's Failed/Partial chip — the only place a failure
// reason was visible before this was 3 clicks deep (expand campaign -> expand run -> hover
// one recipient's badge in RunDetailTable), so a hover on the headline chip itself came up
// empty. Recipients rarely fail for a single uniform reason (a bad number vs. an opted-out
// number vs. a paused low-quality sender are all genuinely different problems), so this
// groups by reason and counts rather than picking just one — "Failed" alone doesn't answer
// "why", but "3x No contact number on file, 1x Opted out" does, without opening anything.
function failureSummary(run) {
  if (!run?.failed?.length) return "";
  const counts = new Map();
  run.failed.forEach((f) => {
    const reason = f.reason || "Unknown reason";
    counts.set(reason, (counts.get(reason) || 0) + 1);
  });
  return [...counts.entries()].map(([reason, count]) => (count > 1 ? `${count}x ${reason}` : reason)).join("\n");
}

// Compact per-run breakdown for the table row — the same sent/delivered/read/failed story
// as the accordion used to tell, just dense enough to fit inline instead of needing to
// open anything first.
function DeliveryMiniStats({ run }) {
  if (!run) return <Typography sx={{ fontSize: 12, color: "#B0B7BF" }}>—</Typography>;
  const delivered = (run.sent || []).filter((s) => s.status === "delivered" || s.status === "read").length;
  const read = (run.sent || []).filter((s) => s.status === "read").length;
  return (
    <Stack direction="row" spacing={1.4} alignItems="center">
      <Tooltip title="Sent">
        <Stack direction="row" spacing={0.3} alignItems="center">
          <DoneRoundedIcon sx={{ fontSize: 14, color: "#6B7280" }} />
          <Typography sx={{ fontSize: 12 }}>{run.sentCount}</Typography>
        </Stack>
      </Tooltip>
      <Tooltip title="Delivered">
        <Stack direction="row" spacing={0.3} alignItems="center">
          <DoneAllRoundedIcon sx={{ fontSize: 14, color: "#6B7280" }} />
          <Typography sx={{ fontSize: 12 }}>{delivered}</Typography>
        </Stack>
      </Tooltip>
      <Tooltip title="Read">
        <Stack direction="row" spacing={0.3} alignItems="center">
          <DoneAllRoundedIcon sx={{ fontSize: 14, color: "#53BDEB" }} />
          <Typography sx={{ fontSize: 12, color: "#53BDEB", fontWeight: 700 }}>{read}</Typography>
        </Stack>
      </Tooltip>
      {run.failedCount > 0 && (
        <Tooltip title="Failed">
          <Stack direction="row" spacing={0.3} alignItems="center">
            <ErrorOutlineRoundedIcon sx={{ fontSize: 14, color: "#E34C14" }} />
            <Typography sx={{ fontSize: 12, color: "#E34C14", fontWeight: 700 }}>{run.failedCount}</Typography>
          </Stack>
        </Tooltip>
      )}
    </Stack>
  );
}

function RunDetailTable({ run }) {
  const rows = [
    ...(run.sent || []).map((s) => ({ ...s, outcome: "sent" })),
    ...(run.failed || []).map((f) => ({ ...f, outcome: "failed" })),
  ];
  if (!rows.length) {
    return <Typography sx={{ fontSize: 12.5, color: "#9CA3AF", py: 1 }}>No recipients recorded for this run.</Typography>;
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF" }}>Creator</TableCell>
          <TableCell sx={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF" }}>Status</TableCell>
          <TableCell sx={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF" }}>Replied</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={r.influencerId || i}>
            <TableCell sx={{ fontSize: 12.5, fontFamily: "Quicksand" }}>{r.name || "—"}</TableCell>
            <TableCell>
              {/* Two genuinely different failure kinds share the same red "Failed" badge:
                  outcome "failed" never even got sent (a pre-send validation failure —
                  opted out, quality-paused number, etc — reason always set by
                  helpers/whatsappHelper.js). outcome "sent" with status "failed" WAS
                  accepted by Meta and only failed delivery afterward, reported live via the
                  webhook (see routes/whatsappCampaigns.js's enrichCampaignsWithLiveStatus) —
                  that reason is often null since Meta doesn't always include one. Either
                  way, a bare "Failed" with nothing to hover was the actual bug report. */}
              {(r.outcome === "failed" || r.status === "failed") ? (
                <Tooltip title={r.reason || "No reason recorded for this failure"}>
                  <span><StatusBadge status="failed" /></span>
                </Tooltip>
              ) : (
                <StatusBadge status={r.status} />
              )}
            </TableCell>
            <TableCell>
              {r.outcome === "failed" ? (
                <Typography sx={{ fontSize: 12.5, color: "#B0B7BF" }}>—</Typography>
              ) : r.replied ? (
                <Stack direction="row" spacing={0.4} alignItems="center">
                  <ReplyRoundedIcon sx={{ fontSize: 14, color: "#09AC31" }} />
                  <Typography sx={{ fontSize: 12.5, color: "#09AC31", fontWeight: 700 }}>Yes</Typography>
                </Stack>
              ) : (
                <Typography sx={{ fontSize: 12.5, color: "#9CA3AF" }}>No</Typography>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// One dense row per campaign (WATI's broadcast-list layout) — click the chevron to drop
// down into every run this campaign has had, each independently expandable into its own
// recipient-level breakdown, same depth of detail the old accordion offered.
function CampaignTableRow({ campaign, numberLabel, onRun, onDelete, running }) {
  const [open, setOpen] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState(null);
  const lastRun = campaign.runs?.[campaign.runs.length - 1];
  const repliedCount = (lastRun?.sent || []).filter((s) => s.replied).length;
  const audience = audienceLabel(campaign);
  const status = campaignStatus(campaign);
  const failureText = failureSummary(lastRun);

  return (
    <>
      <TableRow hover sx={{ "& > td": { borderBottom: open ? "none" : undefined } }}>
        <TableCell sx={{ width: 36, pr: 0 }}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowDownIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontWeight: 700, fontFamily: "Quicksand", fontSize: 13.5 }}>{campaign.name}</Typography>
          <Typography sx={{ fontSize: 11.5, color: "#9CA3AF" }}>
            {campaign.templateId?.name || "—"}
            {numberLabel && ` · ${numberLabel}`}
          </Typography>
          {/* Visibility is org-wide now (a teammate's campaigns show up alongside your
              own) — only worth a line when it's NOT you, same as omitting "by you" on
              your own rows elsewhere in the app. */}
          {!campaign.isMine && campaign.createdBy?.fullName && (
            <Typography sx={{ fontSize: 10.5, color: "#B0B7BF" }}>Created by {campaign.createdBy.fullName}</Typography>
          )}
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.6} alignItems="center">
            <GroupsOutlinedIcon sx={{ fontSize: 15, color: "#9CA3AF" }} />
            <Box>
              <Typography sx={{ fontSize: 12.5 }}>{audience.label}</Typography>
              <Typography sx={{ fontSize: 10.5, color: "#B0B7BF" }}>{audience.sub}</Typography>
            </Box>
          </Stack>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontSize: 12.5 }}>{formatDateTime(campaign.lastRunAt)}</Typography>
          {lastRun?.runBy?.fullName && (
            <Typography sx={{ fontSize: 10.5, color: "#B0B7BF" }}>by {lastRun.runBy.fullName}</Typography>
          )}
          {campaign.runs?.length > 1 && (
            <Typography sx={{ fontSize: 10.5, color: "#B0B7BF" }}>{campaign.runs.length} runs total</Typography>
          )}
        </TableCell>
        <TableCell>
          <DeliveryMiniStats run={lastRun} />
        </TableCell>
        <TableCell>
          {repliedCount > 0 ? (
            <Stack direction="row" spacing={0.4} alignItems="center">
              <ReplyRoundedIcon sx={{ fontSize: 14, color: "#09AC31" }} />
              <Typography sx={{ fontSize: 12.5, color: "#09AC31", fontWeight: 700 }}>{repliedCount}</Typography>
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 12.5, color: "#B0B7BF" }}>—</Typography>
          )}
        </TableCell>
        <TableCell>
          <Tooltip title={<Box sx={{ whiteSpace: "pre-line" }}>{failureText}</Box>} disableHoverListener={!failureText}>
            <Chip size="small" label={status.label} sx={{ bgcolor: status.bg, color: status.color, fontWeight: 700, fontSize: 10.5 }} />
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            {/* Seeing a teammate's campaign (org-wide visibility) doesn't mean you can run
                or delete it — the backend enforces this too, this just avoids a
                confirm-then-403 round trip. */}
            <Tooltip title={campaign.isMine ? "" : "Only this campaign's creator can run it"}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={running ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : <PlayArrowIcon sx={{ fontSize: 16 }} />}
                  onClick={() => onRun(campaign._id)}
                  disabled={running || !campaign.isMine}
                  sx={{ textTransform: "none", boxShadow: "none", fontSize: 12.5, px: 1.4, bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
                >
                  Run
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={campaign.isMine ? "" : "Only this campaign's creator can delete it"}>
              <span>
                <IconButton size="small" onClick={() => onDelete(campaign._id, campaign.name)} disabled={!campaign.isMine}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0, border: "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: 3, py: 2, bgcolor: "#FAFBFC", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
              {(!campaign.runs || campaign.runs.length === 0) ? (
                <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>Never run yet.</Typography>
              ) : (
                <Stack spacing={1}>
                  {[...campaign.runs].reverse().map((run) => {
                    const isRunOpen = expandedRunId === run._id;
                    return (
                      <Box key={run._id} sx={{ borderRadius: "10px", border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden", bgcolor: "#fff" }}>
                        <Box
                          onClick={() => setExpandedRunId(isRunOpen ? null : run._id)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 1.5,
                            py: 1,
                            cursor: "pointer",
                            "&:hover": { bgcolor: "#F3F4F6" },
                          }}
                        >
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {isRunOpen ? <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#9CA3AF" }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />}
                            <Typography sx={{ fontSize: 13 }}>{formatDateTime(run.runAt)}</Typography>
                            {run.runBy?.fullName && (
                              <Typography sx={{ fontSize: 11.5, color: "#9CA3AF" }}>· by {run.runBy.fullName}</Typography>
                            )}
                          </Stack>
                          <DeliveryMiniStats run={run} />
                        </Box>
                        <Collapse in={isRunOpen}>
                          <Box sx={{ px: 1.5, pb: 1 }}>
                            <RunDetailTable run={run} />
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// Who's currently blocked from receiving template sends, and why — invisible otherwise;
// a send just fails for them with no obvious place to check the reason or undo it.
function OptedOutPanel() {
  const { enqueueSnackbar } = useSnackbar();
  const { data } = useOptedOutConversations();
  const clearMutation = useClearOptOut();
  const [open, setOpen] = useState(false);

  const conversations = data?.conversations || [];
  if (!conversations.length) return null;

  const handleClear = async (conversationId, name) => {
    if (!window.confirm(`Re-enable messaging to ${name || conversationId}? Only do this if they've confirmed they want messages again.`)) return;
    try {
      await clearMutation.mutateAsync(conversationId);
      enqueueSnackbar("Re-enabled — future sends to this number will go through again", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Failed to clear opt-out", { variant: "error" });
    }
  };

  return (
    <Box sx={{ borderRadius: "12px", border: "1px solid rgba(227,76,20,0.25)", bgcolor: "rgba(227,76,20,0.06)", mb: 3, overflow: "hidden" }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, cursor: "pointer" }}
      >
        <BlockRoundedIcon sx={{ fontSize: 17, color: "#E34C14" }} />
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#E34C14", fontFamily: "Quicksand" }}>
          {conversations.length} opted out — blocked from template sends
        </Typography>
        {open ? <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#E34C14", ml: "auto" }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: "#E34C14", ml: "auto" }} />}
      </Box>
      <Collapse in={open}>
        <Table size="small">
          <TableBody>
            {conversations.map((c) => (
              <TableRow key={c.conversationId}>
                <TableCell sx={{ fontSize: 12.5, fontFamily: "Quicksand", border: "none" }}>
                  {c.influencerDbId?.userName || c.influencerDbId?.username || c.customerName || c.customerWaId}
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: "#6B7280", border: "none" }}>{c.ourDisplayPhoneNumber}</TableCell>
                <TableCell sx={{ fontSize: 12, color: "#6B7280", border: "none" }}>{formatDateTime(c.optedOutAt)}</TableCell>
                <TableCell align="right" sx={{ border: "none" }}>
                  <Tooltip title="Re-enable — only if they've confirmed they want messages again">
                    <IconButton size="small" onClick={() => handleClear(c.conversationId, c.influencerDbId?.userName || c.customerName)}>
                      <RestartAltRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Collapse>
    </Box>
  );
}

const STATUS_FILTERS = [
  { value: "all", label: "All campaigns" },
  { value: "never", label: "Never run" },
  { value: "completed", label: "Completed" },
  { value: "issues", label: "Had failures" },
];

// Same value strings as Plans' own owner filter (Plans.jsx's OWNER_OPTIONS) for
// consistency across the app, even though the UI control here matches this page's own
// Select-based filter style rather than Plans' bespoke button+menu.
const OWNER_FILTERS = [
  { value: "anyone", label: "Everyone" },
  { value: "me", label: "By me" },
];

function WhatsAppCampaignsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading } = useWhatsappCampaigns();
  const { data: numbersData } = useWhatsappNumbers();
  const runMutation = useRunWhatsappCampaign();
  const deleteMutation = useDeleteWhatsappCampaign();
  const [runningId, setRunningId] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [numberFilter, setNumberFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("anyone");

  const campaigns = useMemo(() => data?.campaigns || [], [data]);
  const numbers = useMemo(() => numbersData?.tokens || [], [numbersData]);
  // A campaign's own record only stores its template's wabaId, not a display number — same
  // lookup the Templates page uses to answer "which number does this belong to".
  const numberLabelByWaba = useMemo(
    () => Object.fromEntries(numbers.map((n) => [n.wabaId, n.displayPhoneNumber])),
    [numbers]
  );

  // Aggregated across every run of every campaign — the channel's whole track record,
  // not just whatever the most recent send happened to look like.
  const stats = useMemo(() => {
    let totalSent = 0, totalFailed = 0, totalDelivered = 0, totalRead = 0, totalReplied = 0;
    for (const c of campaigns) {
      for (const run of c.runs || []) {
        totalSent += run.sentCount || 0;
        totalFailed += run.failedCount || 0;
        for (const s of run.sent || []) {
          if (s.status === "delivered" || s.status === "read") totalDelivered += 1;
          if (s.status === "read") totalRead += 1;
          if (s.replied) totalReplied += 1;
        }
      }
    }
    const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);
    return {
      totalCampaigns: campaigns.length,
      totalSent,
      totalFailed,
      deliveredPct: pct(totalDelivered, totalSent),
      readPct: pct(totalRead, totalSent),
      repliedPct: pct(totalReplied, totalSent),
    };
  }, [campaigns]);

  const filteredCampaigns = useMemo(() => {
    const q = search.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (q && !`${c.name} ${c.templateId?.name || ""}`.toLowerCase().includes(q)) return false;
      if (numberFilter !== "all" && c.templateId?.wabaId !== numberFilter) return false;
      if (ownerFilter === "me" && !c.isMine) return false;
      if (statusFilter === "never" && c.runs?.length) return false;
      if (statusFilter === "completed") {
        const last = c.runs?.[c.runs.length - 1];
        if (!last || last.failedCount) return false;
      }
      if (statusFilter === "issues") {
        const last = c.runs?.[c.runs.length - 1];
        if (!last?.failedCount) return false;
      }
      return true;
    });
  }, [campaigns, search, statusFilter, numberFilter, ownerFilter]);

  const handleRun = async (id) => {
    setRunningId(id);
    try {
      const res = await runMutation.mutateAsync(id);
      enqueueSnackbar(
        `Sent to ${res.sent.length} influencer${res.sent.length === 1 ? "" : "s"}${res.failed.length ? `, ${res.failed.length} failed` : ""}`,
        { variant: res.failed.length ? "warning" : "success" }
      );
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Failed to run campaign", { variant: "error" });
    } finally {
      setRunningId(null);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete campaign "${name}"?`)) return;
    try {
      await deleteMutation.mutateAsync(id);
      enqueueSnackbar("Campaign deleted", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Failed to delete campaign", { variant: "error" });
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)" }}>
      <DashboardHeaderSection hideSearch />
      <Container sx={{ marginTop: 8, pb: 6, pl: { xs: 2, sm: 3, lg: "108px" }, pr: { xs: 2, sm: 3 } }} maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 22, display: "flex", alignItems: "center", gap: 1 }}>
            <WhatsAppIcon sx={{ color: "#25D366" }} /> WhatsApp Campaigns
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.25 }}>
            Template broadcasts sent to your Plans' influencers, with live delivery and reply tracking.
          </Typography>
        </Box>

        {!isLoading && campaigns.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mb: 2.5 }}>
            <StatCard icon={CampaignOutlinedIcon} label="Campaigns" value={stats.totalCampaigns} bg="rgba(37,99,235,0.12)" color="#2563EB" />
            <StatCard icon={SendRoundedIcon} label="Messages sent" value={stats.totalSent} sub={stats.totalFailed ? `${stats.totalFailed} failed` : "0 failed"} bg="rgba(9,172,49,0.12)" color="#09AC31" />
            <StatCard icon={DoneAllRoundedIcon} label="Delivered" value={`${stats.deliveredPct}%`} bg="rgba(107,114,128,0.12)" color="#6B7280" />
            <StatCard icon={DoneAllRoundedIcon} label="Read" value={`${stats.readPct}%`} bg="rgba(83,189,235,0.15)" color="#53BDEB" />
            <StatCard icon={ReplyRoundedIcon} label="Replied" value={`${stats.repliedPct}%`} bg="rgba(9,172,49,0.12)" color="#09AC31" />
          </Stack>
        )}

        <NumberHealthStrip numbers={numbers} />

        <OptedOutPanel />

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : campaigns.length === 0 ? (
          <Paper sx={{ borderRadius: "12px", border: "1px solid rgba(0,0,0,0.07)", py: 6, textAlign: "center" }}>
            <Typography sx={{ fontFamily: "Quicksand", color: "#6B7280" }}>
              No campaigns yet — send a WhatsApp template to a Plan's influencers from the Plan's Summary page to
              create one, reusable any time from here.
            </Typography>
          </Paper>
        ) : (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
              <TextField
                size="small"
                placeholder="Search by campaign or template name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ bgcolor: "#fff", borderRadius: "8px", flex: 1, maxWidth: { sm: 340 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ bgcolor: "#fff", borderRadius: "8px" }}>
                  {STATUS_FILTERS.map((f) => (
                    <MenuItem key={f.value} value={f.value} sx={{ fontSize: 13.5 }}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} sx={{ bgcolor: "#fff", borderRadius: "8px" }}>
                  {OWNER_FILTERS.map((f) => (
                    <MenuItem key={f.value} value={f.value} sx={{ fontSize: 13.5 }}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {numbers.length > 1 && (
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <Select value={numberFilter} onChange={(e) => setNumberFilter(e.target.value)} displayEmpty sx={{ bgcolor: "#fff", borderRadius: "8px" }}>
                    <MenuItem value="all" sx={{ fontSize: 13.5 }}>All numbers</MenuItem>
                    {numbers.map((n) => (
                      <MenuItem key={n.wabaId} value={n.wabaId} sx={{ fontSize: 13.5 }}>
                        {n.displayPhoneNumber}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>

            {filteredCampaigns.length === 0 ? (
              <Paper sx={{ borderRadius: "12px", border: "1px solid rgba(0,0,0,0.07)", py: 5, textAlign: "center" }}>
                <Typography sx={{ fontFamily: "Quicksand", color: "#6B7280" }}>No campaigns match this search/filter.</Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: "12px", border: "1px solid rgba(0,0,0,0.07)", boxShadow: "none" }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ "& th": { fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.3, bgcolor: "#FAFBFC", borderBottom: "1px solid rgba(0,0,0,0.06)" } }}>
                      <TableCell sx={{ width: 36 }} />
                      <TableCell>Campaign</TableCell>
                      <TableCell>Audience</TableCell>
                      <TableCell>Last run</TableCell>
                      <TableCell>Delivery</TableCell>
                      <TableCell>Replied</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCampaigns.map((c) => (
                      <CampaignTableRow
                        key={c._id}
                        campaign={c}
                        numberLabel={numberLabelByWaba[c.templateId?.wabaId]}
                        onRun={handleRun}
                        onDelete={handleDelete}
                        running={runningId === c._id}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}

export default WhatsAppCampaignsPage;
